import { useEffect, useState } from 'react';
import {
    Box,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import CustomModal from 'src/components/modal/customModal';
import { styled, useTheme } from '@mui/material/styles';
import FormProvider, { RHFCheckbox, RHFMultiCheckbox, RHFRadioGroup, RHFSelect, RHFTextField } from 'src/components/hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import { renderErrorMessage, renderSuccessMessage } from 'src/lib/toastMessage';
import { axiosGetCall, axiosPostCall } from 'src/services/apisCall';
import { SMS, allowedCountries } from 'src/lib/Enums';
import { checkButtonAccess } from 'src/utils/common';
import { jwtDecode } from 'jwt-decode';

const InputContainer = styled('div')(() => ({
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    gap: '20px',
}));
const FieldHeading = styled(Typography)(() => ({
    fontWeight: 500,
}));

const AddSmsPlan = ({ openModal, handleCloseModal, title, forceLoad, updateUserId }) => {
    const [canSubmit, setCanSubmit] = useState(true);

    const { email } = jwtDecode(localStorage.getItem('token'));

    const theme = useTheme();

    const Schema = Yup.object().shape({
        title: Yup.string().required('Title is required'),
        country: Yup.string().required('Country is required'),
        deduction: Yup.string().required('Deduction is required'),
        SingleUnicode: Yup.number().typeError('Must be a number').required('Single Unicode is required'),
        SingleEnglish: Yup.number().typeError('Must be a number').required('Single English is required'),
        multiUnicode: Yup.number().typeError('Must be a number').required('Multi Unicode is required'),
        multiEnglish: Yup.number().typeError('Must be a number').required('Multi English is required'),
        transactional: Yup.number().typeError('Must be a number').required('Transactional price is required'),
        promotional: Yup.number().typeError('Must be a number').required('Promotional price is required'),
        explicit: Yup.number().typeError('Must be a number').required('Service Explicit price is required'),
        implicit: Yup.number().typeError('Must be a number').required('Service Implicit price is required'),
        dlt: Yup.number().typeError('Must be a number').required('DLT price is required'),
        Reseller: Yup.number().typeError('Must be a number').required('Reseller price is required'),
    });

    const methods = useForm({
        resolver: yupResolver(Schema),
        defaultValues: {
            title: '',
            country: '',
            deduction: 'submission',
            SingleUnicode: '',
            SingleEnglish: '',
            multiUnicode: '',
            multiEnglish: '',
            transactional: '',
            promotional: '',
            explicit: '',
            implicit: '',
            dlt: '',
            Reseller: '',
        },
    });

    const { control, watch, setValue, handleSubmit, reset, formState: { errors } } = methods;

    // Count total validation errors
    const totalErrors = Object.keys(errors).length;


    const onSubmit = async (data) => {
        setCanSubmit(false);
        try {
            const payload = {
                "title": data.title,
                "countryCode": data.country,
                "deductionType": data.deduction,
                "singlePartUnicodeCharLimit": data.SingleUnicode,
                "singlePartEnglishCharLimit": data.SingleEnglish,
                "multiPartUnicodeCharLimit": data.multiUnicode,
                "multiPartEnglishCharLimit": data.multiEnglish,
                "transactionalSmsPrice": data.transactional,
                "promotionalSmsPrice": data.promotional,
                "explicitSmsPrice": data.explicit,
                "implicitSmsPrice": data.implicit,
                "DLTSmsPrice": data.dlt,
                "resellerOverSellingX": data.Reseller
            };
            await axiosPostCall('addSmsPlan', payload);
            renderSuccessMessage('Plan added successfully');
            handleCloseModal();
            reset();
            forceLoad(true);

        } catch (error) {
            // logger.error('Add Gateway [handleSubmit]', error);
            const errorMessage = error?.message;
            renderErrorMessage(errorMessage);
        } finally {
            setCanSubmit(true);
        }
    };
    const onCancel = () => {
        reset();
    };

    const description = (
        <Box>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Stack
                    direction="column"
                    gap={2}
                    sx={{ p: '25px', overflowY: 'auto', maxHeight: '40rem', height: '100%', width: '800px' }}
                >
                    <Box display="flex" justifyContent="space-between" gap="20px">
                        <InputContainer>
                            <RHFTextField size="large" name="title" label="Title" placeholder="Enter Title." />
                        </InputContainer>
                        <InputContainer>
                            <RHFSelect name="country" label="Select Country" control={control}>
                                {Object.entries(allowedCountries).map(([key, value]) => (
                                    <MenuItem key={key} value={key}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </RHFSelect>
                        </InputContainer>
                    </Box>
                    <Typography
                        id="modal-modal-title"
                        variant="h6"
                        component="h2"
                        sx={{ color: 'grey.10' }}>
                        Single part message char
                    </Typography>
                    <Box display="flex" justifyContent="space-between" gap="20px">
                        <InputContainer>
                            <RHFTextField type="number" size="large" name="SingleUnicode" label="Unicode" placeholder="Enter Unicode." />
                        </InputContainer>
                        <InputContainer>
                            <RHFTextField type="number" size="large" name="SingleEnglish" label="English" placeholder="Enter English." />
                        </InputContainer>
                    </Box>
                    <Typography
                        id="modal-modal-title"
                        variant="h6"
                        component="h2"
                        sx={{ color: 'grey.10' }}>
                        Multi part message char
                    </Typography>
                    <Box display="flex" justifyContent="space-between" gap="20px">
                        <InputContainer>
                            <RHFTextField type="number" size="large" name="multiUnicode" label="Unicode" placeholder="Enter Unicode." />
                        </InputContainer>
                        <InputContainer>
                            <RHFTextField type="number" size="large" name="multiEnglish" label="English" placeholder="Enter English." />
                        </InputContainer>
                    </Box>
                    <Typography
                        id="modal-modal-title"
                        variant="h6"
                        component="h2"
                        sx={{ color: 'grey.10' }}>
                        Route Price
                    </Typography>
                    <Box display="flex" justifyContent="space-between" gap="20px">
                        <InputContainer>
                            <RHFTextField type="number" size="large" name="transactional" label="Transactional" placeholder="Enter Transactional Price." />
                        </InputContainer>
                        <InputContainer>
                            <RHFTextField type="number" size="large" name="promotional" label="Promotional" placeholder="Enter Promotional Price." />
                        </InputContainer>
                    </Box>
                    <Box display="flex" justifyContent="space-between" gap="20px">
                        <InputContainer>
                            <RHFTextField type="number" size="large" name="explicit" label="Service Explicit" placeholder="Enter Service Explicit Price." />
                        </InputContainer>
                        <InputContainer>
                            <RHFTextField type="number" size="large" name="implicit" label="Service Implicit" placeholder="Enter Service Implicit Price." />
                        </InputContainer>
                    </Box>
                    <Box display="flex" width="49%" >
                        <InputContainer>
                            <RHFTextField type="number" size="large" name="dlt" label="DLT" placeholder="Enter DLT Price." />
                        </InputContainer>
                    </Box>
                    <InputContainer>
                        <RHFTextField type="number" size="large" name="Reseller" label="Reseller (Overselling)" placeholder="Enter reseller overselling multiplication factor" />
                    </InputContainer>
                    <InputContainer>
                        <Typography sx={{ mr: 1 }}>Deduction Type</Typography>
                        <RHFRadioGroup
                            name="deduction"
                            row
                            spacing={2}
                            container={{ display: 'flex', alignItems: 'center' }}
                            options={[
                                { value: "submission", label: 'Submission Based' },
                                { value: "delivery", label: 'Delivery Based' },
                            ]}
                        />
                    </InputContainer>
                    {totalErrors > 0 && (
                        <Typography sx={{ color: theme.palette.error.main }}>
                            Please fill all required fields!
                        </Typography>
                    )}


                        <Box sx={{ display: 'flex', gap: '1rem', mt: 3 }}>
                            <Box
                                sx={{
                                    width: '7.625rem',
                                    backgroundColor: canSubmit ? '#212B36' : 'grey',
                                    borderRadius: '8px',
                                    p: '0.562rem',
                                    height: '2.375rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    pointerEvents: canSubmit ? 'auto' : 'none',
                                }}
                                onClick={handleSubmit(onSubmit)}
                            >
                                <Typography sx={{ fontWeight: 600, color: '#fff' }}>
                                    {updateUserId ? "Update" : "Submit"}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    width: '7.625rem',
                                    backgroundColor: '#fff',
                                    borderRadius: '8px',
                                    p: '0.562rem',
                                    height: '2.375rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                }}
                                onClick={onCancel}
                            >
                                <Typography sx={{ fontWeight: 600, color: '#212B36' }}>Cancel</Typography>
                            </Box>
                        </Box>

                </Stack>
            </FormProvider>
        </Box>
    );

    return (
        openModal && (
            <CustomModal
                open={openModal}
                title="Add New SMS Plan"
                description={description}
                close={handleCloseModal}
                isCloseIcon={true}
            />
        )
    );
};

export default AddSmsPlan;
